import { useState, useEffect, useRef } from "react"
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import {} from "react-router-dom"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { usePermissions } from "@/hooks/use-permissions"
import { usePersistedState } from "@/lib/use-persisted-state"
import { useClientsSearch } from "@/hooks/use-clients-search"
import { ClientAvatar } from "@/components/client-avatar"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { Pagination } from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SearchIcon, ChevronDownIcon, ChevronUpIcon, ChevronsUpDownIcon,
  TagsIcon, PencilIcon, XIcon, UploadIcon, ReceiptIcon,
} from "lucide-react"
import { Avatar, AvatarFallback, AvatarGroup } from "@/components/ui/avatar"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { cn } from "@/lib/utils"
import { type BillingStatus, BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, ALL_BILLING_STATUSES } from "@/lib/billing-status"
import { parseCSVRows, escapeHTML } from "@/lib/csv"
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { BillingImportDialog } from "./BillingImportDialog"
import { DELIVERY_NOTE_STATUS_LABEL, DELIVERY_NOTE_STATUS_CLASS, type DeliveryNoteStatus } from "./ShipmentDetailUtils"
import { buildBillingCsv, selectBillingExportRows } from "./ShipmentsBillingUtils"

// ─── Types ────────────────────────────────────────────────────────────────────

type ShipmentStatus = "en_planificacion" | "planificado" | "activo" | "en_curso" | "finalizado" | "cancelado"
type WorkLine       = "nacional" | "internacional" | "batea"

interface ShipmentRow {
  id:                            string
  internalId:                    number
  status:                        ShipmentStatus
  workLine:                      WorkLine | null
  externalId:                    string | null
  notes:                         string | null
  billingNotes:                  string | null
  scheduledOriginArrivalAt:      string | null
  deliveryNoteNumber:            string | null
  deliveryNoteStatus:            DeliveryNoteStatus
  loadingWeightKg:               string | null
  unloadingWeightKg:             string | null
  deletedAt:                     string | null
  clientId:                      string | null
  clientName:                    string | null
  clientAvatarKey:               string | null
  productNames:                  string | null
  firstTripId:                   string | null
  firstTripMobileNumber:         number | null
  mobileNumbers:                 string | null
  tripCount:                     number
  driverNames:                   string | null
  truckPlates:                   string | null
  trailerPlates:                 string | null
  senderName:                    string | null
  senderCityName:                string | null
  recipientName:                 string | null
  recipientCityName:             string | null
  billingStatus:                 "controlado" | "liquidado" | "facturado" | "cerrado" | null
  billingTags:                   string | null
  cancelReason:                  string | null
  cancelDescription:             string | null
  operatorNames:                 string | null
}

interface ClientOption { id: string; name: string; code: string }

// ─── Status config ────────────────────────────────────────────────────────────

const ALL_STATUSES: ShipmentStatus[] = [
  "en_planificacion", "planificado", "activo", "en_curso", "finalizado", "cancelado",
]

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  en_planificacion: "En planificación",
  planificado:      "Planificado",
  activo:           "Activo",
  en_curso:         "En curso",
  finalizado:       "Finalizado",
  cancelado:        "Cancelado",
}

const STATUS_COLORS: Record<ShipmentStatus, string> = {
  en_planificacion: "bg-muted text-muted-foreground",
  planificado:      "bg-blue-100 text-blue-700",
  activo:           "bg-emerald-100 text-emerald-700",
  en_curso:         "bg-orange-100 text-orange-700",
  finalizado:       "bg-slate-100 text-slate-600",
  cancelado:        "bg-red-100 text-red-700",
}

const PAGE_SIZE_OPTIONS = [25, 50, 100]

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700",
  internacional: "bg-purple-100 text-purple-700",
  batea:         "bg-amber-100 text-amber-700",
}


// ─── Tag filter autocomplete ────────────────────────────────────────────────

function TagFilterInput({
  value, onChange, onSelect,
}: {
  value: string
  onChange: (v: string) => void
  onSelect: (v: string) => void
}) {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [open, setOpen]               = useState(false)
  const debounceRef                   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef                  = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.length < 2) { setSuggestions([]); setOpen(false); return }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authorizedFetch(`${API_URL}/api/shipments/billing-tags?q=${encodeURIComponent(value)}`)
        const data = await res.json() as string[]
        setSuggestions(data)
        setOpen(data.length > 0)
      } catch { setSuggestions([]); setOpen(false) }
    }, 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [value])

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <TagsIcon className="absolute left-2 top-1.5 size-3 text-muted-foreground z-10" />
      <Input
        placeholder="Filtrar por etiqueta..."
        className="h-7 pl-6 text-xs w-44"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true) }}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false) }}
      />
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-44 rounded-md border bg-popover shadow-md overflow-hidden">
          {suggestions.map(s => (
            <button
              key={s}
              type="button"
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
              onMouseDown={(e) => { e.preventDefault(); onSelect(s); setOpen(false) }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Quick-edit dialog ───────────────────────────────────────────────────────

type QuickEditField = "tags" | "billingNotes"

interface QuickEditState {
  id:         string
  internalId: number
  field:      QuickEditField
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ShipmentsBillingPage() {
  const { can } = usePermissions()
  const qc      = useQueryClient()

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState("")
  const [page,            setPage]            = useState(1)
  const [pageSize,        setPageSize]        = usePersistedState<number>("shipments-billing:pageSize", 25)
  const [filterStatuses,  setFilterStatuses]  = usePersistedState<ShipmentStatus[]>("shipments-billing:filterStatuses", ["finalizado"])
  const [filterBilling,   setFilterBilling]   = usePersistedState<BillingStatus[]>("shipments-billing:filterBillings", [])
  const [filterNoteStatuses, setFilterNoteStatuses] = useState<DeliveryNoteStatus[]>([])
  const [filterClientId,  setFilterClientId]  = usePersistedState<string>("shipments-billing:filterClientId", "")
  const [filterTagInput,      setFilterTagInput]      = useState("")
  const [filterWorkLines,     setFilterWorkLines]     = usePersistedState<WorkLine[]>("shipments-billing:filterWorkLines", [])
  const [filterOperatorNames, setFilterOperatorNames] = usePersistedState<string[]>("shipments-billing:filterOperatorNames", [])
  const [dateFrom,            setDateFrom]            = usePersistedState("shipments-billing:dateFrom", "")
  const [dateTo,              setDateTo]              = usePersistedState("shipments-billing:dateTo", "")
  const [sortField,       setSortField]       = useState<"internalId" | "scheduledOriginArrivalAt">("scheduledOriginArrivalAt")
  const [sortDir,         setSortDir]         = useState<"asc" | "desc">("desc")

  const [exportPreview,      setExportPreview]      = useState<{ csvText: string; filename: string; truncated?: boolean } | null>(null)

  const [billingImportOpen, setBillingImportOpen] = useState(false)
  const [selectedIds,    setSelectedIds]    = useState<Set<string>>(new Set())
  // Keep selected row data across server-side pagination so exports include rows from every page.
  const [selectedRows,   setSelectedRows]   = useState<Map<string, ShipmentRow>>(new Map())
  const [quickEdit,      setQuickEdit]      = useState<QuickEditState | null>(null)
  const [qeTags,         setQeTags]         = useState<string[]>([])
  const [qeNotes,        setQeNotes]        = useState<string>("")
  const [qeError,        setQeError]        = useState<string | null>(null)
  const [bulkBillingPending, setBulkBillingPending] = useState(false)
  const [bulkBillingConfirm, setBulkBillingConfirm] = useState<{ status: BillingStatus; title: string; description: string } | null>(null)


  function openQuickEdit(d: ShipmentRow, field: QuickEditField) {
    const tags = d.billingTags ? d.billingTags.split("||").map(t => t.trim()).filter(Boolean) : []
    setQeTags(tags)
    setQeNotes(d.billingNotes ?? "")
    setQeError(null)
    setQuickEdit({ id: d.id, internalId: d.internalId, field })
  }

  // Client search
  const { onSearchChange: onClientSearchChange, clients: clientSearchResults } = useClientsSearch()
  const { data: clientOptions = [] } = useQuery<ClientOption[]>({
    queryKey: ["clients-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/clients?pageSize=500&deleted=false`)
      const body = await res.json() as { data?: ClientOption[] }
      return body.data ?? []
    },
    staleTime: 60_000,
  })
  const clientSelectOptions = [
    { value: "__none__", label: "Todos los clientes" },
    ...(clientSearchResults.length > 0 ? clientSearchResults : clientOptions).map((c: ClientOption) => ({
      value: c.id,
      label: c.name,
      sublabel: c.code,
    })),
  ]

  useEffect(() => { setPage(1) }, [search, filterStatuses, filterBilling, filterNoteStatuses, filterClientId, filterWorkLines, filterOperatorNames, dateFrom, dateTo, sortField, sortDir, pageSize])

  // ── Query ──────────────────────────────────────────────────────────────────
  const { data: operatorList = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["operators-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/users/operators`)
      return res.json()
    },
    staleTime: 5 * 60_000,
  })
  const uniqueOperatorNames = operatorList.map(o => o.name).filter(Boolean).sort((a, b) => a.localeCompare(b))

  const { data: shipmentsResponse, isLoading } = useQuery({
    queryKey: ["shipments-billing", page, pageSize, search, filterStatuses, filterBilling, filterNoteStatuses, filterClientId, filterWorkLines, filterOperatorNames, dateFrom, dateTo, sortField, sortDir],
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set("deleted", "false")
      params.set("page",     String(page))
      params.set("pageSize", String(pageSize))
      params.set("sortField", sortField)
      params.set("sortDir",   sortDir)
      if (search.trim())              params.set("search",  search.trim())
      if (filterStatuses.length > 0 && filterStatuses.length < ALL_STATUSES.length)
                                          params.set("statuses", filterStatuses.join(","))
      if (filterBilling.length > 0)         params.set("billingStatuses", filterBilling.join(","))
      if (filterNoteStatuses.length > 0)   params.set("noteStatuses", filterNoteStatuses.join(","))
      if (filterClientId)                  params.set("clientId", filterClientId)
      if (filterWorkLines.length > 0)      params.set("workLines", filterWorkLines.join(","))
      if (filterOperatorNames.length > 0)  params.set("operatorNames", filterOperatorNames.join(","))
      if (dateFrom)                         params.set("dateFrom", dateFrom)
      if (dateTo)                           params.set("dateTo",   dateTo)
      const res = await authorizedFetch(`${API_URL}/api/shipments?${params}`)
      return res.json() as Promise<{ data: ShipmentRow[]; total: number }>
    },
  })

  const shipmentsData = shipmentsResponse?.data ?? []
  const serverTotal   = shipmentsResponse?.total ?? 0
  const filtered      = shipmentsData
  const totalPages    = Math.max(1, Math.ceil(serverTotal / pageSize))

  // Refresh cached row data when a selected row is loaded again after pagination or a refetch.
  useEffect(() => {
    if (shipmentsData.length === 0) return
    setSelectedRows(prev => {
      let changed = false
      const next = new Map(prev)
      for (const row of shipmentsData) {
        if (selectedIds.has(row.id) && next.get(row.id) !== row) {
          next.set(row.id, row)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [shipmentsData, selectedIds])

  // ── Quick-edit mutation ────────────────────────────────────────────────────
  const quickEditMut = useMutation({
    mutationFn: async ({ id, billingTags, billingNotes }: { id: string; billingTags?: string[]; billingNotes?: string }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          ...(billingTags   !== undefined && { billingTags }),
          ...(billingNotes  !== undefined && { billingNotes: billingNotes || null }),
        }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipments-billing"] })
      setQuickEdit(null)
      toast.success("Guardado")
    },
    onError: (err: Error) => setQeError(err.message),
  })

  function handleQuickEditSave() {
    if (!quickEdit) return
    if (quickEdit.field === "tags") {
      quickEditMut.mutate({ id: quickEdit.id, billingTags: qeTags })
    } else {
      quickEditMut.mutate({ id: quickEdit.id, billingNotes: qeNotes })
    }
  }

  // ── Billing status mutation ────────────────────────────────────────────────
  const billingMut = useMutation({
    mutationFn: async ({ id, billingStatus }: { id: string; billingStatus: BillingStatus | null }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ billingStatus }),
      })
    },
    onMutate: async ({ id, billingStatus }) => {
      await qc.cancelQueries({ queryKey: ["shipments-billing"] })
      const prev = qc.getQueriesData({ queryKey: ["shipments-billing"], exact: false })
      qc.setQueriesData({ queryKey: ["shipments-billing"], exact: false }, (old: unknown) => {
        const o = old as { data: ShipmentRow[]; total: number } | undefined
        if (!o) return o
        return { ...o, data: o.data.map(r => r.id === id ? { ...r, billingStatus } : r) }
      })
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) ctx.prev.forEach(([key, val]) => qc.setQueryData(key, val))
      toast.error("No se pudo actualizar el estado de facturación")
    },
  })

  function requestBulkBillingStatus(status: BillingStatus) {
    if (!can("write", "shipments") || selectedIds.size === 0) return
    const label = BILLING_STATUS_LABELS[status]
    setBulkBillingConfirm({
      status,
      title: "Cambiar facturación",
      description: `¿Cambiás la facturación de ${selectedIds.size} carga${selectedIds.size !== 1 ? "s" : ""} a «${label}»?\n\nTambién se actualizarán todos los viajes asociados a ${selectedIds.size === 1 ? "esa carga" : "esas cargas"}.`,
    })
  }

  async function handleBulkBillingStatus(status: BillingStatus) {
    setBulkBillingPending(true)
    try {
      await authorizedFetch(`${API_URL}/api/shipments/bulk-status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [...selectedIds], billingStatus: status === "pendiente" ? null : status }),
      })
      await qc.invalidateQueries({ queryKey: ["shipments-billing"] })
      toast.success("Facturación actualizada")
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBulkBillingPending(false)
    }
  }

  // ── Sort toggle ────────────────────────────────────────────────────────────
  function toggleSort(field: typeof sortField) {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortField(field); setSortDir("desc") }
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  const allSelected  = filtered.length > 0 && filtered.every(r => selectedIds.has(r.id))
  const someSelected = filtered.some(r => selectedIds.has(r.id)) && !allSelected

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        filtered.forEach(r => next.delete(r.id))
        return next
      })
      setSelectedRows(prev => {
        const next = new Map(prev)
        filtered.forEach(r => next.delete(r.id))
        return next
      })
      return
    }

    setSelectedIds(prev => {
      const next = new Set(prev)
      filtered.forEach(r => next.add(r.id))
      return next
    })
    setSelectedRows(prev => {
      const next = new Map(prev)
      filtered.forEach(r => next.set(r.id, r))
      return next
    })
  }

  // ── Export ─────────────────────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false)

  async function handleExport() {
    const date = new Date().toISOString().slice(0, 10)
    const filename = `facturacion-cargas-${date}.csv`

    if (selectedIds.size > 0) {
      // Selected rows are accumulated while navigating server-side pagination.
      const rows = selectBillingExportRows(selectedRows, selectedIds)
      setExportPreview({ csvText: buildBillingCsv(rows), filename })
      return
    }

    // No selection — fetch ALL matching records from the server (paginated, 500/page)
    const EXPORT_PAGE_SIZE = 500
    const EXPORT_MAX_ROWS  = 2000

    setIsExporting(true)
    try {
      const baseParams = new URLSearchParams()
      baseParams.set("deleted",   "false")
      baseParams.set("pageSize",  String(EXPORT_PAGE_SIZE))
      baseParams.set("sortField", sortField)
      baseParams.set("sortDir",   sortDir)
      if (search.trim())                   baseParams.set("search",        search.trim())
      if (filterStatuses.length > 0 && filterStatuses.length < ALL_STATUSES.length)
                                           baseParams.set("statuses",      filterStatuses.join(","))
      if (filterBilling.length > 0)         baseParams.set("billingStatuses", filterBilling.join(","))
      if (filterNoteStatuses.length > 0)   baseParams.set("noteStatuses", filterNoteStatuses.join(","))
      if (filterClientId)                  baseParams.set("clientId",      filterClientId)
      if (filterWorkLines.length > 0)      baseParams.set("workLines",     filterWorkLines.join(","))
      if (filterOperatorNames.length > 0)  baseParams.set("operatorNames", filterOperatorNames.join(","))
      if (dateFrom)                        baseParams.set("dateFrom",      dateFrom)
      if (dateTo)                          baseParams.set("dateTo",        dateTo)

      // First page — also tells us the total
      baseParams.set("page", "1")
      const first = await authorizedFetch(`${API_URL}/api/shipments?${baseParams}`)
      const firstBody = await first.json() as { data: ShipmentRow[]; total: number }
      const allRows: ShipmentRow[] = [...firstBody.data]
      const effectiveTotal = Math.min(firstBody.total, EXPORT_MAX_ROWS)
      const totalPages = Math.ceil(effectiveTotal / EXPORT_PAGE_SIZE)

      // Fetch remaining pages sequentially up to the row cap
      for (let p = 2; p <= totalPages; p++) {
        baseParams.set("page", String(p))
        const res  = await authorizedFetch(`${API_URL}/api/shipments?${baseParams}`)
        const body = await res.json() as { data: ShipmentRow[]; total: number }
        allRows.push(...body.data)
      }

      const truncated = firstBody.total > EXPORT_MAX_ROWS
      setExportPreview({ csvText: buildBillingCsv(allRows), filename, truncated })
    } catch (err) {
      toast.error("No se pudo generar el export")
    } finally {
      setIsExporting(false)
    }
  }

  function downloadCsv(csvText: string, filename: string) {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  function downloadXls(csvText: string, filename: string) {
    const rows = parseCSVRows(csvText)
    const tableHtml = `<table>${rows.map((row, ri) =>
      `<tr>${row.map(cell => ri === 0 ? `<th>${escapeHTML(cell)}</th>` : `<td>${escapeHTML(cell)}</td>`).join("")}</tr>`
    ).join("")}</table>`
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"/></head><body>${tableHtml}</body></html>`
    const blob = new Blob([html], { type: "application/vnd.ms-excel" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = filename.replace(/\.csv$/, ".xls")
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Tag filter — debounce into search ────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setSearch(filterTagInput); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [filterTagInput])

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Facturación de cargas" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-72">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por #ID, remitente, destinatario, cliente, obs..."
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          <div className="flex items-center gap-2">
            {can("write", "shipments") && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setBillingImportOpen(true)}>
                <UploadIcon className="size-3.5" />
                Importar facturación
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExport} disabled={isExporting}>
              <TagsIcon className="size-3.5" />
              {isExporting ? "Generando..." : selectedIds.size > 0 ? `Exportar ${selectedIds.size} seleccionadas` : `Exportar todo (${serverTotal})`}
            </Button>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2">

            {/* Client */}
            <div className="w-48">
              <SearchableSelect
                compact
                options={clientSelectOptions}
                value={filterClientId || "__none__"}
                onValueChange={(v) => { setFilterClientId(v === "__none__" ? "" : v); setPage(1) }}
                onSearchChange={onClientSearchChange}
                placeholder="Todos los clientes"
                searchPlaceholder="Buscar cliente..."
              />
            </div>

            {/* Work lines */}
            <div className="w-px h-5 bg-border" />
            {(["nacional", "internacional", "batea"] as WorkLine[]).map(wl => (
              <button key={wl} type="button"
                onClick={() => { setFilterWorkLines(prev => prev.includes(wl) ? prev.filter(x => x !== wl) : [...prev, wl]); setPage(1) }}
                className={cn(
                  "h-7 inline-flex items-center text-xs px-2.5 rounded-full font-medium transition-colors",
                  filterWorkLines.includes(wl)
                    ? WORK_LINE_COLORS[wl]
                    : "bg-background text-muted-foreground border border-border hover:bg-muted",
                )}
              >
                {WORK_LINE_LABELS[wl]}
              </button>
            ))}
            {filterWorkLines.length > 0 && (
              <button type="button" onClick={() => { setFilterWorkLines([]); setPage(1) }}
                className="h-7 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                limpiar
              </button>
            )}

            {/* Operators */}
            <div className="w-px h-5 bg-border" />
            <SearchableSelect
              multiple
              compact
              options={uniqueOperatorNames.map(n => ({ value: n, label: n }))}
              value={filterOperatorNames}
              onValueChange={(v) => { setFilterOperatorNames(v); setPage(1) }}
              placeholder="Todos los operadores"
              searchPlaceholder="Buscar operador..."
            />

            {/* Page size — ml-auto */}
            <div className="flex items-center gap-1 ml-auto shrink-0">
              {PAGE_SIZE_OPTIONS.map(size => (
                <button
                  key={size}
                  type="button"
                  onClick={() => { setPageSize(size); setPage(1) }}
                  className={cn(
                    "h-7 w-8 text-xs rounded transition-colors",
                    pageSize === size
                      ? "bg-foreground text-background font-semibold"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: status chips + tag filter */}
          <div className="flex flex-wrap items-center gap-1.5">
            {ALL_STATUSES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setFilterStatuses(prev =>
                    prev.includes(s)
                      ? prev.length === 1 ? prev : prev.filter(x => x !== s)
                      : [...prev, s]
                  )
                  setPage(1)
                }}
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity",
                  STATUS_COLORS[s],
                  filterStatuses.includes(s) ? "opacity-100" : "opacity-30",
                )}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
            {filterStatuses.length < ALL_STATUSES.length && (
              <button
                type="button"
                onClick={() => { setFilterStatuses([...ALL_STATUSES]); setPage(1) }}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                ver todos
              </button>
            )}
            <div className="w-px h-5 bg-border" />
            {/* Delivery note status — multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" size="sm" className="h-7 w-36 justify-between text-xs font-normal">{filterNoteStatuses.length === 0 ? "Estado remito" : filterNoteStatuses.length === 1 ? DELIVERY_NOTE_STATUS_LABEL[filterNoteStatuses[0]!] : `${filterNoteStatuses.length} estados`} <ChevronDownIcon className="size-3" /></Button>} />
              <DropdownMenuContent align="start" className="w-48">
                {(["normal", "validado", "reclamado"] as DeliveryNoteStatus[]).map(status => (
                  <DropdownMenuItem key={status} onClick={(e) => { e.preventDefault(); setFilterNoteStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]); setPage(1) }}>
                    <input type="checkbox" readOnly checked={filterNoteStatuses.includes(status)} className="size-3.5 accent-primary" />
                    {DELIVERY_NOTE_STATUS_LABEL[status]}
                  </DropdownMenuItem>
                ))}
                {filterNoteStatuses.length > 0 && <DropdownMenuItem onClick={() => { setFilterNoteStatuses([]); setPage(1) }}>Limpiar selección</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="w-px h-5 bg-border" />
            {/* Billing status — multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" size="sm" className="h-7 w-36 justify-between text-xs font-normal">{filterBilling.length === 0 ? "Facturación" : filterBilling.length === 1 ? BILLING_STATUS_LABELS[filterBilling[0]!] : `${filterBilling.length} estados`} <ChevronDownIcon className="size-3" /></Button>} />
              <DropdownMenuContent align="start" className="w-48">
                {ALL_BILLING_STATUSES.map(status => (
                  <DropdownMenuItem key={status} onClick={(e) => { e.preventDefault(); setFilterBilling(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]); setPage(1) }}>
                    <input type="checkbox" readOnly checked={filterBilling.includes(status)} className="size-3.5 accent-primary" />
                    {BILLING_STATUS_LABELS[status]}
                  </DropdownMenuItem>
                ))}
                {filterBilling.length > 0 && <DropdownMenuItem onClick={() => { setFilterBilling([]); setPage(1) }}>Limpiar selección</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="w-px h-5 bg-border" />
            {/* Tag filter with autocomplete */}
            <TagFilterInput
              value={filterTagInput}
              onChange={(v) => { setFilterTagInput(v); if (!v) { setSearch(""); setPage(1) } }}
              onSelect={(v) => { setFilterTagInput(v); setSearch(v); setPage(1) }}
            />
            <div className="w-px h-5 bg-border" />
            {/* Dates */}
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark]"
              />
              <span className="text-xs text-muted-foreground">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark]"
              />
              {(dateFrom || dateTo) && (
                <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); setPage(1) }}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                  limpiar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Selection bar ── */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-20 flex items-center gap-2 rounded-lg border bg-background/95 backdrop-blur-sm px-3 py-2 shadow-md">
            <span className="flex items-center gap-1.5 shrink-0">
              <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tabular-nums">
                {selectedIds.size}
              </span>
              <span className="text-sm font-medium">
                carga{selectedIds.size !== 1 ? "s" : ""} seleccionada{selectedIds.size !== 1 ? "s" : ""}
              </span>
            </span>
            <div className="w-px self-stretch bg-border mx-1" />

            {can("write", "shipments") && (
              <DropdownMenu>
                <DropdownMenuTrigger render={
                  <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs" disabled={bulkBillingPending}>
                    <ReceiptIcon className="size-3" /> Facturación <ChevronDownIcon className="size-3" />
                  </Button>
                } />
                <DropdownMenuContent align="start" className="w-44">
                  {ALL_BILLING_STATUSES.map(status => (
                    <DropdownMenuItem key={status} onClick={() => requestBulkBillingStatus(status)}>
                      <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[status].split(" ")[0])} />
                      {BILLING_STATUS_LABELS[status]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button
              variant="ghost" size="sm"
              className="h-7 w-7 p-0 ml-auto shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSelectedIds(new Set())
                setSelectedRows(new Map())
              }}
              title="Deseleccionar todo"
            >
              <XIcon className="size-3.5" />
            </Button>
          </div>
        )}

        {/* ── Top pagination ── */}
        {serverTotal > 0 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {serverTotal > 0
                ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, serverTotal)} de ${serverTotal} resultado${serverTotal !== 1 ? "s" : ""}`
                : "0 resultados"
              }
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <span className="tabular-nums">{page} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Table ── */}
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-9 pr-0">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected }}
                    onChange={toggleSelectAll}
                    className="size-3.5 rounded border-border cursor-pointer accent-primary"
                  />
                </TableHead>
                <TableHead className="w-14">
                  <button type="button" onClick={() => toggleSort("internalId")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                    N°
                    {sortField === "internalId"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="w-24">
                  <button type="button" onClick={() => toggleSort("scheduledOriginArrivalAt")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                    Fecha
                    {sortField === "scheduledOriginArrivalAt"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead>Choferes / móvil</TableHead>
                <TableHead>Camión / semi</TableHead>
                <TableHead>Remitente → Ciudad</TableHead>
                <TableHead>Destinatario → Ciudad</TableHead>
                <TableHead className="w-20 text-right">Tons.</TableHead>
                <TableHead className="w-32">Remito / estado</TableHead>
                <TableHead className="w-36">Facturación</TableHead>
                <TableHead>Etiquetas</TableHead>
                <TableHead>Obs. facturación</TableHead>
                <TableHead>Obs. carga</TableHead>

              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={15} className="h-24 text-center text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} className="h-24 text-center text-muted-foreground">
                    No se encontraron cargas.
                  </TableCell>
                </TableRow>
              ) : filtered.map(d => (
                <TableRow
                  key={d.id}
                  className={cn(
                    "hover:bg-muted/40 transition-colors",
                    selectedIds.has(d.id) && "bg-muted/50",
                    d.deletedAt && "opacity-50",
                  )}
                >
                  {/* Checkbox */}
                  <TableCell className="pr-0">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(d.id)}
                      onChange={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev)
                          next.has(d.id) ? next.delete(d.id) : next.add(d.id)
                          return next
                        })
                        setSelectedRows(prev => {
                          const next = new Map(prev)
                          next.has(d.id) ? next.delete(d.id) : next.set(d.id, d)
                          return next
                        })
                      }}
                      className="size-3.5 rounded border-border cursor-pointer accent-primary"
                    />
                  </TableCell>

                  {/* N° */}
                  <TableCell className="font-mono text-xs text-primary font-semibold">
                    #{d.internalId}
                  </TableCell>

                  {/* Cliente */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {d.clientId && <ClientAvatar clientId={d.clientId} clientName={d.clientName ?? ""} />}
                    </div>
                  </TableCell>

                  {/* Fecha */}
                  <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {d.scheduledOriginArrivalAt
                      ? new Date(d.scheduledOriginArrivalAt).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" })
                      : <span>—</span>}
                  </TableCell>

                  {/* Choferes / móvil */}
                  <TableCell>
                    {(() => {
                      const driverList = d.driverNames?.split("||").map(n => n.trim()).filter(Boolean) ?? []
                      const lastDriver = driverList[driverList.length - 1]
                      return driverList.length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <AvatarGroup>
                            {driverList.map(name => (
                              <Avatar key={name} size="sm">
                                <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                                  {avatarInitials(name)}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                          </AvatarGroup>
                          <div className="flex flex-col gap-0.5 text-xs min-w-0">
                            <span className="truncate" title={lastDriver}>{lastDriver}</span>
                            <span className="text-muted-foreground">Móvil: {d.mobileNumbers?.split("||").map(n => n.trim()).filter(Boolean).join(", ") || "—"}</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )
                    })()}
                  </TableCell>

                  {/* Patentes */}
                  <TableCell>
                    <div className="flex flex-col gap-0.5 text-xs font-mono">
                      <span>🚚 {d.truckPlates?.split("||").map(p => p.trim()).filter(Boolean).join(", ") || "—"}</span>
                      <span>▣ {d.trailerPlates?.split("||").map(p => p.trim()).filter(Boolean).join(", ") || "—"}</span>
                    </div>
                  </TableCell>

                  {/* Remitente */}
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium leading-tight">{d.senderName ?? "—"}</span>
                      {d.senderCityName && <span className="text-xs text-muted-foreground">→ {d.senderCityName}</span>}
                    </div>
                  </TableCell>

                  {/* Destinatario */}
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium leading-tight">{d.recipientName ?? "—"}</span>
                      {d.recipientCityName && <span className="text-xs text-muted-foreground">→ {d.recipientCityName}</span>}
                    </div>
                  </TableCell>

                  {/* Tons. */}
                  <TableCell className="text-right text-sm tabular-nums">
                    {d.unloadingWeightKg
                      ? `${(parseFloat(d.unloadingWeightKg) / 1000).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>

                  {/* Remito + validación */}
                  <TableCell className="text-xs font-mono">
                    <div className="flex flex-col gap-1">
                      <span>{d.deliveryNoteNumber ?? "—"}</span>
                      <span className={cn("inline-flex w-fit rounded-full px-1.5 py-0.5 text-[10px] font-sans font-medium", DELIVERY_NOTE_STATUS_CLASS[d.deliveryNoteStatus ?? "normal"])}>
                        {DELIVERY_NOTE_STATUS_LABEL[d.deliveryNoteStatus ?? "normal"]}
                      </span>
                    </div>
                  </TableCell>

                  {/* Facturación — clickeable */}
                  <TableCell>
                    {can("write", "shipments") && !d.deletedAt ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger render={
                          <button className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity",
                            BILLING_STATUS_COLORS[d.billingStatus ?? "pendiente"],
                          )}>
                            {BILLING_STATUS_LABELS[d.billingStatus ?? "pendiente"]}
                          </button>
                        } />
                        <DropdownMenuContent align="start" className="w-44">
                          {ALL_BILLING_STATUSES.filter(s => s !== (d.billingStatus ?? "pendiente")).map(s => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => billingMut.mutate({ id: d.id, billingStatus: s === "pendiente" ? null : s as BillingStatus })}
                            >
                              <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[s].split(" ")[0])} />
                              {BILLING_STATUS_LABELS[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                        BILLING_STATUS_COLORS[d.billingStatus ?? "pendiente"],
                      )}>
                        {BILLING_STATUS_LABELS[d.billingStatus ?? "pendiente"]}
                      </span>
                    )}
                  </TableCell>

                  {/* Etiquetas — click para editar */}
                  <TableCell>
                    <div
                      className={cn(
                        "group flex flex-wrap gap-1 min-w-16 min-h-6 rounded px-1 -mx-1 cursor-pointer",
                        can("write", "shipments") && !d.deletedAt && "hover:bg-muted/60 transition-colors",
                      )}
                      onClick={() => can("write", "shipments") && !d.deletedAt && openQuickEdit(d, "tags")}
                      title={can("write", "shipments") ? "Editar etiquetas" : undefined}
                    >
                      {d.billingTags
                        ? d.billingTags.split("||").map((tag, i) => (
                            <span key={i} className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
                              {tag.trim()}
                            </span>
                          ))
                        : can("write", "shipments") && !d.deletedAt
                          ? <span className="text-muted-foreground/40 text-[10px] italic group-hover:text-muted-foreground transition-colors">+ agregar</span>
                          : <span className="text-muted-foreground">—</span>
                      }
                    </div>
                  </TableCell>

                  {/* Obs. facturación — click para editar */}
                  <TableCell className="max-w-48">
                    <div
                      className={cn(
                        "group flex items-start gap-1 min-h-6 rounded px-1 -mx-1 cursor-pointer",
                        can("write", "shipments") && !d.deletedAt && "hover:bg-muted/60 transition-colors",
                      )}
                      onClick={() => can("write", "shipments") && !d.deletedAt && openQuickEdit(d, "billingNotes")}
                      title={can("write", "shipments") ? "Editar observaciones" : undefined}
                    >
                      {d.billingNotes
                        ? <span className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap flex-1">{d.billingNotes}</span>
                        : can("write", "shipments") && !d.deletedAt
                          ? <span className="text-muted-foreground/40 text-[10px] italic group-hover:text-muted-foreground transition-colors">+ agregar</span>
                          : <span className="text-muted-foreground text-xs">—</span>
                      }
                      {d.billingNotes && can("write", "shipments") && !d.deletedAt && (
                        <PencilIcon className="size-3 shrink-0 mt-0.5 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-colors" />
                      )}
                    </div>
                  </TableCell>

                  {/* Obs. carga */}
                  <TableCell className="max-w-48">
                    <span className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">{d.notes || "—"}</span>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* ── Bottom pagination ── */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={serverTotal}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>

      {/* ── Quick-edit dialog ── */}
      {quickEdit && (
        <Dialog open onOpenChange={(open) => { if (!open) setQuickEdit(null) }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {quickEdit.field === "tags" ? "Etiquetas de facturación" : "Observaciones de facturación"}
                <span className="ml-2 text-sm font-normal text-muted-foreground">#{quickEdit.internalId}</span>
              </DialogTitle>
            </DialogHeader>

            {quickEdit.field === "tags" ? (
              <div className="space-y-3 py-2">
                <div className="flex flex-wrap gap-1.5 min-h-8">
                  {qeTags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2.5 py-0.5 text-xs font-medium">
                      {tag}
                      <button
                        type="button"
                        onClick={() => setQeTags(prev => prev.filter((_, j) => j !== i))}
                        className="hover:text-violet-900 transition-colors leading-none"
                      >
                        <XIcon className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <Input
                  placeholder="Agregar etiqueta y presionar Enter o coma..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      const val = (e.target as HTMLInputElement).value.trim()
                      if (val && !qeTags.includes(val)) setQeTags(prev => [...prev, val])
                      ;(e.target as HTMLInputElement).value = ""
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">Presioná Enter o coma para agregar</p>
              </div>
            ) : (
              <div className="space-y-2 py-2">
                <Label>Observaciones</Label>
                <textarea
                  rows={4}
                  value={qeNotes}
                  onChange={(e) => setQeNotes(e.target.value)}
                  placeholder="Notas para el área de facturación..."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
            )}

            {qeError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{qeError}</p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickEdit(null)}>Cancelar</Button>
              <Button onClick={handleQuickEditSave} disabled={quickEditMut.isPending}>
                {quickEditMut.isPending ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Export preview dialog ── */}
      {exportPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background rounded-lg shadow-xl flex flex-col w-full max-w-4xl max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">Vista previa — {exportPreview.filename}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {Math.max(0, parseCSVRows(exportPreview.csvText).length - 1)} filas
                </p>
                {exportPreview.truncated && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    ⚠️ El export está limitado a 2000 filas. Hay más resultados — usá los filtros para acotar la selección o exportá por selección manual.
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setExportPreview(null)} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="text-xs w-full border-collapse">
                <thead>
                  {parseCSVRows(exportPreview.csvText).slice(0, 1).map((row, ri) => (
                    <tr key={ri} className="bg-muted font-semibold">
                      {row.map((cell, ci) => (
                        <th key={ci} className="px-2 py-1 border-r last:border-r-0 whitespace-nowrap text-left font-semibold">{cell}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {parseCSVRows(exportPreview.csvText).slice(1).map((row, ri) => (
                    <tr key={ri} className="border-t hover:bg-muted/30">
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-2 py-1 border-r last:border-r-0 whitespace-nowrap">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <button type="button" onClick={() => setExportPreview(null)} className="text-sm text-muted-foreground hover:text-foreground px-4 py-2">
                Cerrar
              </button>
              <Button variant="outline" onClick={() => downloadXls(exportPreview.csvText, exportPreview.filename)}>
                Descargar XLS
              </Button>
              <Button onClick={() => downloadCsv(exportPreview.csvText, exportPreview.filename)}>
                Descargar CSV
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm billing status change dialog ── */}
      <Dialog open={!!bulkBillingConfirm} onOpenChange={(open) => { if (!open) setBulkBillingConfirm(null) }}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{bulkBillingConfirm?.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-line">{bulkBillingConfirm?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkBillingConfirm(null)}>Cancelar</Button>
            <Button
              disabled={bulkBillingPending}
              onClick={async () => {
                const status = bulkBillingConfirm?.status
                setBulkBillingConfirm(null)
                if (status) await handleBulkBillingStatus(status)
              }}
            >
              {bulkBillingPending ? "Guardando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BillingImportDialog open={billingImportOpen} onOpenChange={setBillingImportOpen} />
    </>
  )
}
